/** API 요청·응답 계약. **`apps/api/src/routes/jobs.ts` 의 실제 형태에서 뽑았다.**
 * 추측한 엔드포인트를 여기 적지 않는다 — 여기 있으면 클라이언트가 그것을 부른다.
 *
 * 경로를 문자열로 화면에 흩뿌리지 않고 이 파일의 빌더로만 만든다. 서버가 경로를 바꾸면
 * 고칠 자리가 한 곳이다.
 */

import type { PaymentHistoryQuery } from "./billing-history";
import { PAYMENT_HISTORY_PARAMS } from "./billing-history";
import type {
  DetailJobView,
  IdentityProvider,
  JobEventView,
  JobImageView,
  JobVersionFileView,
  JobVersionView,
  RecallDecision,
} from "./domain";
import type { DateRangeContext, JobSearchFilter } from "./search";
import { JOB_SEARCH_PARAMS, deviceUtcOffsetMinutes, resolveDateRange } from "./search";

export interface CreateJobResponse {
  jobId: string;
}

/** `POST /api/jobs/:jobId/images` 의 응답. 새 행이면 201, 이미 있던 사진이면 200. */
export interface UploadImageResponse {
  imageId: string;
  /** 같은 작업에 같은 내용의 사진이 이미 있어 행을 늘리지 않았다는 뜻(200). */
  duplicate?: boolean;
}

/** 실패 응답의 `code`. **`JobFailureCode` 와 다른 축이다** — 이쪽은 요청이 거절된 이유이고
 * 그쪽은 장·작업에 저장되는 실패 사유다. `NOT_CONFIGURED` 만 양쪽에 있다. */
export type ApiErrorCode =
  /** 503. provider 키가 없어 누끼만 끊겼다. 업로드·조회는 살아 있다. */
  | "NOT_CONFIGURED"
  /** 409. 이미 처리 중이다. 다시 걸면 같은 장을 두 번 과금하므로 서버가 막는다. */
  | "ALREADY_PROCESSING"
  /** 409. **다시 만들기 요청이 들고 온 호출 횟수가 지금 값과 다르다**(`RemakeImageRequest`).
   *  그 사이에 그 장이 provider 로 한 번 더 나갔다는 뜻이다.
   *
   * **`이미 다시 만들었다` 가 아니라 `네 숫자가 낡았다` 로 이름 붙인 이유**: 값을 밀어 올리는
   * 것이 다시 만들기만이 아니다. 같은 장의 되살리기(`retryImage`)와 배치 처리(`process`)도
   * 같은 칸을 올린다. `ALREADY_REMADE` 라고 부르면 **되살리기가 올려 둔 값 때문에 거절된
   * 요청이 "이미 다시 만들었다"고 거짓말한다.** 코드는 원인을 정확히 말하고, 셀러에게 보일
   * 문구는 화면이 고른다(`ApiErrorCode` 는 셀러가 보는 낱말이 아니다).
   *
   * **화면이 할 일은 한 가지뿐이다 — 작업을 다시 읽는다.** 그러면 새 결과와 새
   * `providerCallCount` 가 함께 온다. 자동으로 다시 걸지 마라. 이 코드가 막고 있는 것이
   * 정확히 그 자동 재시도다(장당 과금). */
  | "STALE_CALL_COUNT"
  /** 409. **다시 만들 수 있는 장이 아니다.** `DONE` 이 아닌 장(대기·처리 중·실패)에
   *  다시 만들기를 걸었다. 실패한 장은 되살리기(`retryImage`)의 몫이다 — 두 동작이 갈린
   *  자리가 여기다(PRD `7.6.8` · `7.13` D-62). 화면 판정은 `canRemakeImage` 다. */
  | "IMAGE_NOT_DONE"
  /** 409. **그 장의 금액을 돌려주기로 기록됐다(또는 이미 돌려줬다).** 합성이 거절돼 판에서 빠진 장 등에 `ImageRefund`
   *  행이 섰다(2026-11-01. NOT_OWED 로 닫힌 행은 빼고 센다). 그 장은 이 작업에서 다시 만들지 않는다. 받았으면 새 바이트가
   *  청구 거르기에 걸려 다시 만들기가 무료가 된다(새 바이트는 새 구매라는 규칙과 어긋난다). 법무 `L-F80`-5 · 6A.25 ⓓ5.
   *  화면은 그 장에 다시 만들기를 띄우지 않는 것이 먼저이고, 이 코드는 마지막 문이다. 문구는 `remake-messages.ts`. */
  | "IMAGE_REFUND_RECORDED"
  /** 409. **원본이 파기된 작업이다**(`DetailJobStatus.DISCARDED`). 행은 남아 있어도 R2
   *  객체가 접두째 지워져 provider 에 보낼 것이 없다(`lib/purge.ts`).
   *
   * 부르고 나서 `SOURCE_MISSING` 으로 실패하게 두지 않는다. 그러면 **실패한 호출 하나가
   * `providerCallCount` 를 올려** 멀쩡하던 장이 `STALE_CALL_COUNT` 를 만들고, 화면에는
   * 되살릴 수도 없는 실패가 남는다. 호출 전에 끊는다. */
  | "JOB_DISCARDED"
  /** 422. **산출물 배경 팔레트 밖의 값이다**(`ALLOWED_BACKGROUND_HEXES`). 지금 정해진 것은
   *  `BG_PRIMARY`·`BG_ALT` 둘뿐이고(`docs/TEMPLATE.md` 4장), 나머지 두 스와치는 값이 없어
   *  화면이 `미정` 으로 그린다. **없는 색을 저장하면 합성이 그 색으로 칠할 근거가 없다.** */
  | "BACKGROUND_NOT_ALLOWED"
  /** 422. **상품명이 너무 길다.** 상한은 채널이 정하고(`ChannelSpec.productNameMaxLength`),
   *  그 값이 아직 `null` 이라 지금은 `PRODUCT_NAME_HARD_MAX_LENGTH` 가 막는다. */
  | "PRODUCT_NAME_TOO_LONG"
  /** 422. **이 판매처에 없는 블록이다**(`channelHasBlock`). 셀러가 끈 블록과 채널에 애초에
   *  없는 블록은 다른 것이다 — 앞은 켤 수 있고 뒤는 켤 자리가 없다. */
  | "BLOCK_NOT_IN_CHANNEL"
  /** 409. **채널에는 있는데 이 작업에 그 블록 행이 없다.** 작업을 만들 때 한 벌을 깔므로
   *  (`createJob`) 정상 경로에서는 나지 않는다. 그 벌이 깔리기 전에 만들어진 작업이거나,
   *  아직 고르지 않은 판매처의 블록을 건드린 것이다. **아무것도 쓰지 않고 거절한다.** */
  | "BLOCK_NOT_FOUND"
  /** 400. **보낸 사진 목록이 그 작업의 사진 전부와 다르다**(빠졌거나·중복이거나·남의 장).
   *  재정렬은 순번을 통째로 다시 매기는 일이라 부분 목록을 받으면 빠진 장이 남의 자리를
   *  쥔 채 남는다. */
  | "IMAGE_SET_MISMATCH"
  /** 409. **얼릴 결과물이 하나도 없다.** 만든 장이 없거나, 행은 `DONE` 인데 R2 객체가 없다. */
  | "NO_RESULT"
  /** 409. **아직 결과를 못 받은 장이 남아 있다**(`PENDING`·`PROCESSING`). **승인
   *  (`POST /api/jobs/:jobId/approve`)에서만 난다.** 판정은 새로 만들지 않고
   *  `computeJobStatus(images)` 를 `current` 없이 부른 결과를 쓴다. 아직 결판이 안 난
   *  작업만 `UPLOADED` 로 돌아온다(그 함수 안의 `unsettled`).
   *
   * ⛔ **`ALREADY_PROCESSING` 과 다른 축이다.** 그쪽은 `작업이 지금 잠겨 있다` 이고 이쪽은
   * `잠금이 풀렸는데도 안 만들어진 장이 남았다` 다. **잠금이 풀린 뒤에도 남은 장이
   * `PENDING` 으로 있으면 `ALREADY_PROCESSING` 은 안 나고 이 코드가 난다.** 그 구멍이
   * PRD `13.1` **O-62** 였다. 승인이 통과하면 그 판은 먼저 만든 몇 장짜리로 굳는다.
   *
   * ⛔ **`NO_RESULT` 와도 다르다.** 그쪽은 만든 장이 0 이라 얼릴 것이 없다는 뜻이고,
   * 이쪽은 만든 장이 **있는데도** 아직 다 안 끝났다는 뜻이다.
   *
   * **실패한 장은 남은 장이 아니다**(`7.13` D-77 ①. 실패는 결과물을 줄 수 없을 때이고
   * 원인을 묻지 않고 환불한다). **4장 성공 + 3장 실패는 끝난 상태라 승인된다.**
   *
   * **내보내기(`/export`)에는 이 코드가 오지 않는다.** 그쪽은 `준비됨`·`내보냄` 에서만
   * 받으므로(`NOT_EXPORTABLE`) 남은 장이 있는 작업이 애초에 그 문을 못 지난다.
   * **중복으로 막지 않는다.**
   *
   * 화면 대응은 `A-31c` 다. `좋아요` 가 꺼지고, **비활성 사유는 툴팁이 아니라 항상 보이는
   * 글로 선다**(`A-28b`·`A-25c` 규격). 그 글의 자리는 위쪽 `!` 배너와 그 아래 힌트 줄
   * (`아래 좋아요가 꺼진 이유가 위에 적혀 있습니다.`)이고, 하단은 꺼진 `좋아요` 와 주 동작
   * `진행 보기` 둘이다. ⛔ **버튼 밑(`disabledReason`)에는 달지 않는다.** 달면 그 힌트 줄이
   * 거짓이 되고 같은 말이 한 화면에 두 번 선다. */
  | "IMAGES_UNSETTLED"
  /** 409. **내보낼 수 있는 상태가 아니다.** `준비됨`·`내보냄` 에서만 받는다(PRD `7.7.5`).
   *  처리 중이면 `ALREADY_PROCESSING`, 원본이 파기됐으면 `JOB_DISCARDED` 가 온다 — 셋을
   *  하나로 뭉개지 않는 이유는 화면이 할 말이 다르기 때문이다(기다려라 / 못 한다 / 끝났다). */
  | "NOT_EXPORTABLE"
  /** 409. 판 번호를 다른 요청이 먼저 집었고 그쪽 상태가 내 것과 달랐다. 화면이 할 일은
   *  작업을 다시 읽는 것 하나다. */
  | "VERSION_CONFLICT"
  /** 409. **판에 들어갈 장 가운데 지금 배경의 합성본이 아직 없는 장이 있다.** 판은 누끼가 아니라
   *  불변 합성본을 얼린다(DATA_DICTIONARY 25.9). 빠진 장을 누끼로 메우지도, 판에서 조용히 빼지도
   *  않고 여기서 멈춘다. 합성이 영구 거절된 장(`permanentFailures`)은 이 코드의 대상이 아니다.
   *  그 장은 판에서 빠지고 청구하지 않는다.
   *
   *  ⭐ 2026-11-01: 서버가 그 요청 안에서 빠진 합성본을 20초 동안 메운 **뒤에도** 남았을 때만 온다(장이 많음 · 서비스
   *  장애). 화면이 할 일: 같은 승인 · 내보내기를 다시 누르게 한다. 이미 만든 합성본은 남아 이어진다. 메우기 요청을 따로
   *  부를 필요는 없다(불러도 된다). */
  | "COMPOSITION_INCOMPLETE"
  /** 413. **묶음이 한 요청에 담기에 너무 크다.** 파일 수·총 용량 상한을 넘었다. 낱장
   *  경로(`versionFile`)는 그대로 열려 있다. */
  | "BUNDLE_TOO_LARGE"
  /** 409. **이 작업에 사진을 더 담을 수 없다.** 담긴 장이 `MAX_IMAGES_PER_JOB`(15)에 닿았다
   *  (`upload.ts`. 2026-09-23 사용자 확정).
   *
   * **주어는 요청이 아니라 작업이다.** 보낸 파일 하나가 큰 것이 아니라 그 작업이 찬 것이라,
   * 용량 쪽 413(`파일이 크다`)·`BUNDLE_TOO_LARGE`(`묶음이 크다`)와 다른 축이다. 409 인 이유도
   * 그것이다. 거절의 근거가 보낸 것에 있지 않고 **자원의 현재 상태**에 있다
   * (`ALREADY_PROCESSING`·`IMAGE_NOT_DONE` 과 같은 갈래).
   *
   * **화면이 할 일은 둘이다.** ① 애초에 이 코드를 받지 않는 것이 정상이다. 보내기 전에
   * `remainingImageSlots(지금 담긴 장 수)` 로 잘라 낸다. 다 올린 뒤에 실패를 보는 것이
   * `upload.ts` 머리말이 막으려는 상태다. ② 그래도 받았으면(다른 기기가 먼저 채웠다)
   * **작업을 다시 읽는다.** ⛔ 자동으로 다시 보내지 마라. 몇 번을 보내도 같은 답이다.
   *
   * ⛔ **응답 문장을 파싱해 숫자를 꺼내지 마라.** 상한은 `MAX_IMAGES_PER_JOB` 에서 읽는다.
   * 문장은 `design-manager` 가 정하고 바뀔 수 있다.
   *
   * ⛔ **이 코드가 `duplicate: true` 경로를 막으면 안 된다.** 이미 담긴 사진을 다시 올리는
   * 요청은 장수를 늘리지 않는다. 그것을 거절하면 **서버에 있는 사진을 화면이 실패로 그린다**
   * (`upload.ts` 의 `MAX_IMAGES_PER_JOB` 주석). */
  | "TOO_MANY_IMAGES"
  // ── 결제 (2026-09-25) ──────────────────────────────────────────────────────
  /** 503. **결제 설정이 없다.** 시크릿이 안 들어간 배포이거나 PG 가 우리 키를 거절했다.
   *  ⛔ **셀러 잘못이 아니다.** 다시 눌러도 같은 답이 오므로 화면이 재시도를 권하면 안 된다.
   *  누끼의 `NOT_CONFIGURED` 와 같은 갈래이고, 같은 이유로 **조용히 통과시키지 않는다.** */
  | "PAYMENT_NOT_CONFIGURED"
  /** 409. **금액이 서버 계산과 다르다.** 화면이 보낸 수와 서버가 그 작업의 장수·단가로 다시
   *  만든 수가 어긋났다. ⭐ **이 응답이 온 시점에 PG 는 부르지 않았다. 돈은 움직이지 않았다.**
   *
   * 화면이 할 일은 **작업을 다시 읽고 금액을 다시 받는 것**이다(`paymentEndpoints.checkout`).
   * ⛔ 같은 값으로 자동 재시도하지 마라. 몇 번을 보내도 같은 답이다. */
  | "PAYMENT_AMOUNT_MISMATCH"
  /** 409. **주문번호가 이 작업의 것이 아니다.** 서버가 만든 모양이 아니거나 다른 작업을
   *  가리킨다. 화면이 주문번호를 스스로 만들면 이 코드가 온다. */
  | "PAYMENT_ORDER_MISMATCH"
  /** 409. **이 작업은 이미 결제됐다.** 같은 작업에 두 번 청구되는 것을 서버가 막았다.
   *  화면이 할 일은 작업을 다시 읽는 것이고, **결제는 이미 끝나 있다.** */
  | "PAYMENT_ALREADY_PAID"
  /** 409. **청구할 장이 없다.** 담긴 장이 없거나 전부 이미 처리됐다. */
  | "NOTHING_TO_PAY"
  /** 402. **PG 가 결제를 거절했다.** 한도·정지 카드·결제 세션 만료 같은 것이고, **돈은
   *  움직이지 않았다.** 다시 시도할 수 있다(서버가 결제 행을 닫아 다음 주문번호를 열어 둔다).
   *  ⛔ PG 가 준 사유 문장을 화면에 그대로 띄우지 마라. 서버가 내려 주지 않는다. */
  | "PAYMENT_DECLINED"
  /** 502. **PG 호출이 끝나지 못했다.** ⛔ **돈이 움직였는지 우리도 모른다.** 그래서 화면이
   *  "실패했습니다"라고 단정하면 안 된다. 같은 요청을 다시 보내는 것은 안전하다(서버가
   *  `paymentKey` 를 먼저 박아 두어 같은 키로 이어서 복구한다). */
  | "PAYMENT_GATEWAY_FAILED"
  // ── 시작 전 취소 (2026-10-03. `POST /api/payments/cancel/:jobId`. 법무 `L-F51`-4) ──────
  //
  // 정본 표는 `apps/api/src/routes/payments.ts` 의 `CANCEL_FAILURES` 이고 이 다섯은 그 표의
  // 글자를 그대로 옮긴 것이다. 그 밖에 이 주소가 내는 코드는 `PAYMENT_NOT_CONFIGURED`(503,
  // 위) 하나다. 데이터 쪽 뜻은 `docs/DATA_DICTIONARY.md` 41장.
  //
  // ⛔ **`PAYMENT_CANCEL_FAILED` 와 `PAYMENT_CANCEL_UNCONFIRMED` 를 한 문구로 뭉치지 마라.**
  // 앞은 PG 조회로 「돈이 그대로다」가 확인됐고, 뒤는 확인을 못 했다. 뒤를 「실패했습니다」로
  // 단정하면 셀러는 돈이 돌아간 줄 모르고 다시 결제한다. 문구는 아직 없다(`design-manager`).
  /** 409. **취소할 결제가 없다.** 결제가 없거나, 승인 전이거나, 일부가 이미 돌아간 결제다
   *  (시작 전 취소는 **한 번도 돌아간 적 없는 전액**만 받는다). **PG 를 부르지 않았다.**
   *  화면이 할 일은 작업을 다시 읽는 것이다. ⛔ 자동으로 다시 걸지 마라. */
  | "PAYMENT_NOT_CANCELABLE"
  /** 409. **이미 시작됐다.** 처리 잠금이 잡혀 있거나, 한 장이라도 공급자에 나갔거나
   *  (`JobImage.status <> 'PENDING'` 또는 `providerCallCount > 0`), 청구 항목이 한 줄이라도
   *  있다. **PG 를 부르지 않았다. 돈은 그대로다.** 그때부터는 이 취소가 아니라 장별 정산의
   *  일이다. 화면은 「이미 시작했을 때」 상태로 다시 그린다. */
  | "PAYMENT_CANCEL_AFTER_START"
  /** 409. **같은 결제의 다른 취소가 지금 돌고 있다**(다른 기기). PG 를 부르지 않았다.
   *  잠시 뒤 작업을 다시 읽으면 결판이 나 있다. ⛔ 자동으로 다시 걸지 마라. */
  | "PAYMENT_CANCEL_IN_PROGRESS"
  /** 502. **결제사가 취소하지 않았다. 돈은 그대로다.** 두 가지가 다 확인됐을 때만 온다.
   *  PG 가 그 취소 요청을 **확정적으로 거절했고**(코드가 실린 4xx. `isDefinitiveRejection`),
   *  이어서 조회(`find`)한 잔액이 전액 그대로다. 결제 행은 `CAPTURED` 로 되돌려 두므로 셀러는
   *  시작하거나 다시 취소할 수 있다. (그 사이 같은 작업에 다른 결제가 서 있으면 되돌리지 않고
   *  미확정 표지로 남긴다. 한 작업에 잡힌 결제가 둘이 되지 않게 하려는 것이다.)
   *  ⚠️ **시간 초과 · 5xx 뒤에 잔액이 그대로인 것은 이 코드가 아니다.** 그 취소는 PG 에서
   *  늦게 반영될 수 있어 `PAYMENT_CANCEL_UNCONFIRMED` 로 온다. */
  | "PAYMENT_CANCEL_FAILED"
  /** 502. ⛔ **취소됐는지 모른다.** 「실패했습니다」로 단정하지 마라. 결제 행은 미확정 표지
   *  (`CANCELED` · `capturedAmount` 그대로)로 남고, 그동안 이 작업의 처리 · 결제 준비 · 승인
   *  확정은 `PAYMENT_IN_PROGRESS` 를 낸다. 갈래는 이렇다.
   *  - 취소가 시간 초과 · 5xx · 응답 없음으로 끝났다. 조회 잔액이 그대로여도 여기다(늦게 반영될
   *    수 있다). 표지가 선 지 10분(`CANCEL_SETTLE_GRACE_SECONDS`)이 지나도 잔액이 그대로면
   *    다음 맞추기가 「안 취소됐다」로 확정하고 `CAPTURED` 로 되돌린다.
   *  - 조회도 실패했다.
   *  - 잔액이 0 도 전액도 아니다.
   *
   *  **다시 누르는 것은 이중 취소에 대해서는 안전하다.** 서버가 PG 의 지금 상태로 먼저 맞추고,
   *  이미 취소됐으면 두 번 취소하지 않는다. ⚠️ **그러나 다시 누른다고 풀린다는 보장은 없다.**
   *  아래 갈래는 몇 번을 눌러도 같은 답이 오고, 사람이 결제사 관리 화면과 대조해야 풀린다
   *  (`apps/api/src/lib/payment-cancel.ts` 머리말 「맞추기가 저절로 끝나지 않는 표지」 ·
   *  `docs/DATA_DICTIONARY.md` 41.5 ②): 잔액이 0 도 전액도 아님 · PG 가 그 결제 키를 모름 ·
   *  유예가 지난 뒤에도 조회 실패 · 같은 작업에 다른 결제가 서 있어 되돌리기가 막힘 · 결제 키가
   *  없는 표지. 화면이 이 코드를 계속 받으면 「다시 시도」만 권하지 말고 문의 경로를 함께 둔다
   *  (문구는 `design-manager`).
   *
   *  ⛔ **화면이 자동으로 다시 부르는 자리를 만들지 않는다.** 돈이 움직이는 호출이다. 응답을
   *  아예 못 받은 경우(네트워크)도 이 갈래로 읽는다. */
  | "PAYMENT_CANCEL_UNCONFIRMED"
  // ── 처리 게이트 (2026-09-25. `13.1` `O-13` (가) 전액 청구 후 부분취소) ──────
  //
  // ⭐ **결제가 먼저고 그다음이 처리다.** 아래 다섯은 **공급자를 부르는 주소**(`/process` ·
  // 장 단위 `/process` · `/remake`)가 결제를 요구하며 내는 코드다. 서버에 문이 하나이고
  // **앱도 같이 막힌다**(2026-09-25 사용자 결정. 앱으로 결제할 수 없다는 것이 지금 사실이고
  // 그 사실이 화면에 드러나야 `O-50`·`O-51` 이 미뤄지지 않는다).
  //
  // ⛔ **다섯을 한 낱말로 뭉치지 마라.** 셀러가 할 일이 다 다르다 — 결제한다 / 기다린다 /
  // 다시 결제한다 / (돌아간 작업이다) / **담은 장을 덮을 만큼 더 낸다**. 뭉개면 화면이 할
  // 말을 못 고르고 "안 됩니다" 하나로 수렴한다. **문구는 아직 없다** — `design-manager`
  // 소관이다.
  /** 402. **이 작업에 승인된 결제가 없다.** 화면이 할 일은 **결제로 보내는 것**이다.
   *  ⛔ 자동으로 다시 걸지 마라. 결제가 서기 전에는 몇 번을 보내도 같은 답이다.
   *  ⚠️ **앱에서 이 코드를 받는 것이 오늘 정상 경로다.** 앱에는 결제 화면이 없어
   *  (`O-50`·`O-51` 미정) 셀러는 **웹에서 이어서** 결제한다(`A-53`·`PC로 이어서 하기`). */
  | "PAYMENT_REQUIRED"
  /** 409. **이 작업의 돈이 결판나지 않았다. 기다린다.** ⛔ `PAYMENT_REQUIRED` 와 뭉개지
   *  마라 — 이 상태에서 결제 화면으로 보내면 **한 작업에 두 번 결제하려 들게 된다.**
   *
   *  **처리 게이트만 내는 코드가 아니다**(2026-10-03 셋째 판부터). 내는 자리와 사유가 셋이다.
   *  | 자리 | 사유 |
   *  |---|---|
   *  | 처리 게이트(`/process` · 장 단위 `/process` · `/remake`) | 승인이 나갔는데 결과를 모른다(`PENDING` + 결제 키), 또는 시작 전 취소의 **미확정 표지**가 있다 |
   *  | 결제 준비(`GET /api/payments/checkout/:jobId`) · 승인 확정(`POST /api/payments/confirm`) | **미확정 표지**가 있고, 한 번 맞춰 봤는데도 남아 있다(`cancelMarkerBlocks`) |
   *  | 승인 확정 | **승인이 작업 처리 잠금을 못 쥐었다**(처리나 취소가 그 작업에서 돌고 있다). PG 를 부르지 않았다 |
   *
   *  **대부분은 잠시 뒤 풀린다.** 승인 결과가 들어오거나, 잠금이 풀리거나, 표지가 맞춰진다
   *  (취소가 반영된 것이 보이거나, 표지가 선 지 10분이 지나 잔액 그대로가 확정된다).
   *  ⚠️ **그러나 미확정 표지 중에는 저절로 풀리지 않는 갈래가 있다**(`PAYMENT_CANCEL_UNCONFIRMED`
   *  주석의 목록. 사람이 결제사 관리 화면과 대조해야 한다). 그 동안은 몇 번을 보내도 이 코드다.
   *  ⛔ 자동으로 짧게 되풀이해 부르지 마라. 화면은 「확인 중」을 보이고, 오래 남으면 문의
   *  경로를 함께 둔다(문구는 `design-manager`). */
  | "PAYMENT_IN_PROGRESS"
  /** 402. **마지막 결제가 실패로 닫혔다**(카드 거절). 다시 결제할 수 있다. ⛔ 셀러는 자기가
   *  결제를 시도했다는 것을 안다. `PAYMENT_REQUIRED` 로 뭉개면 화면이 **방금 있었던 일을
   *  모르는 것처럼** 말한다. */
  | "PAYMENT_FAILED"
  /** 402. **전액이 돌아간 작업이다.** 잡혀 있는 돈이 없어 처리·되살리기·다시 만들기가 전부
   *  막힌다. **되살리기까지 막는 유일한 사유다** — 그 밖의 경우에 못 만든 장을 되살리는 것은
   *  추가 결제가 아니라 **이미 낸 돈으로 받을 것을 받는 일**이라 서버가 막지 않는다. */
  | "PAYMENT_REFUNDED"
  /** 402. **승인된 결제는 있는데 그 결제가 덮는 장수를 넘었다.**
   *
   * ⭐ **`quoteJobPayment` 는 결제 시점의 대기 장수로 전액을 받는다.** 그 뒤에 사진을 더
   * 담고 처리를 부르면 **더 담은 장도 같이 나간다** — 3장을 결제하고 15장까지 담으면 12장이
   * 공짜였다. 이 코드는 그 자리를 막는다.
   *
   * ⛔ **`PAYMENT_REQUIRED` 와 뭉개지 마라.** 「결제가 없다」와 「결제가 모자라다」는 셀러가
   * 할 일이 다르다 — 앞은 **결제하는 것**이고 뒤는 **덮이는 만큼 더 내거나 담은 장을
   * 줄이는 것**이다. "결제해 주세요"라고만 하면 이미 결제한 셀러에게 방금 있었던 일을
   * 모르는 것처럼 말하게 된다.
   *
   * ⛔ **서버가 잘라서 일부만 처리하지 않는다.** 자르면 **어느 장이 나갈지를 우리가 고르게
   * 되고**, 셀러가 고르지 않은 장에 돈이 붙는다. 그래서 요청 전체를 거절한다. */
  | "PAYMENT_COVERAGE_EXCEEDED"
  // ── 담기가 닫힌 뒤 (2026-09-25. 한 작업 = 한 번 담기 = 한 번 결제) ─────────
  /** 409. **결제가 끝난 작업이라 사진을 더 담을 수 없다.**
   *
   * ⭐ **사용자가 정한 제품 모델이다** (2026-09-25): 사진은 한 작업에 **한 번** 담고, 더
   * 만들고 싶으면 **새 작업**이다. 여러 작업의 결과를 한 상세페이지에 모으는 일은 그
   * 화면(`W-70`)이 한다.
   *
   * ⛔ **`PAYMENT_*` 다섯과 뜻이 반대다.** 그쪽은 「결제가 없거나 모자라서 막힘」이라 셀러가
   * 할 일이 **결제**인데, 이쪽은 「결제가 끝나서 더 못 담음」이라 할 일이 **새 작업**이다.
   * 뭉개면 이미 결제한 셀러를 결제로 다시 보낸다.
   *
   * ⛔ **`TOO_MANY_IMAGES` 와도 다르다.** 그쪽은 장수가 찬 것이라 **다른 작업에서는 그
   * 장이 담긴다**. 이쪽은 장수와 무관하고 **이 작업의 담기가 닫힌 것**이다.
   *
   * **화면이 할 일은 둘이다.** ① 애초에 이 코드를 받지 않는 것이 정상이다 — 결제로 넘어가기
   * 전에 **결제하면 더 못 담는다**고 미리 말한다. ② 그래도 받았으면 **새 작업으로 안내한다.**
   * ⛔ 자동으로 다시 보내지 마라. 몇 번을 보내도 같은 답이다.
   *
   * ⛔ **이 코드가 `duplicate: true` 경로를 막으면 안 된다.** 이미 담긴 사진을 다시 올리는
   * 요청은 장을 늘리지 않는다. 거절하면 **서버에 있는 사진을 화면이 실패로 그린다**
   * (`TOO_MANY_IMAGES` 가 중복 조회 뒤에 서는 것과 같은 이유다). */
  | "UPLOAD_CLOSED_AFTER_PAYMENT"
  // eslint-disable-next-line @typescript-eslint/ban-types
  | (string & {});

/** 실패 응답. 라우트가 `{ error }` 를, 위 두 경우만 `{ error, code }` 를 내려준다. */
export interface ApiErrorBody {
  error: string;
  code?: ApiErrorCode;
}

/** multipart 필드명. 서버가 `form.get("file")` 로 읽는다. */
export const UPLOAD_FILE_FIELD = "file";

/** multipart 필드명. **클라이언트가 순서를 명시적으로 보낸다.**
 * 안 보내면 서버가 도착 순서로 매기는데, 그러면 1번을 다시 올렸을 때 맨 뒤로 가서
 * 대표컷이 바뀐다. 0-based 정수. */
export const UPLOAD_SORT_ORDER_FIELD = "sortOrder";

/** 목록 질의. 조건과 쪽 나누기를 한 인자에 담는다.
 *
 * `filter` 를 빼면 조건 없이 전체다. **거르는 일은 클라이언트가 하지 않는다**. 조건을
 * 받아 온 뒤 화면에서 거르면 두 번째 쪽부터 조건에 안 맞는 행이 섞인다. */
export interface JobListQuery {
  filter?: JobSearchFilter;
  limit?: number;
  offset?: number;
  /** 기간 경계를 만들 기준 시각·시간대. 앱과 웹은 비워 둔다(기기 시간대가 기본값이다).
   *  서버에서 부를 때는 반드시 넘긴다. Workers 는 UTC 라 기본값이 0 이다. */
  dateRange?: DateRangeContext;
  /** 줄마다 대표 축소본 주소(`JobListItemView.thumbUrl`)를 받는다. `true` 일 때만 `thumbs=1` 이 붙는다.
   *
   * ⭐ **opt-in 이다.** 서버는 이 표시를 받을 때만 D1 질의 둘(후보 장 · 받기 판정)을 더 낸다.
   * 썸네일을 그리지 않는 소비자(알림 폴링 · 월 요약 같은)는 보내지 않는다. 보내지 않으면 응답에
   * `thumbUrl` 칸이 없다(`undefined`). ⚠️ 같은 목록이라도 이 값이 다르면 응답 모양이 다르므로
   * 쿼리 캐시 키를 가른다. */
  thumbs?: boolean;
}

/** 실제로 구현된 엔드포인트만 있다. 없는 것을 여기 적으면 클라이언트가 부른다.
 *
 * `GET /api/jobs`(목록)는 **이제 서버에 있고 찾기 조건도 다 읽는다.** 0002 가
 * `DetailJob.sellerId` 를 넣어 "내 작업"을 서버가 추릴 수 있게 되면서 붙었고, 조건은
 * `routes/jobs.ts` 의 `parseStatusTokens`·`parseDateRange` 가 파싱한다. 예전 주석이
 * "라우트가 `limit`·`offset` 만 파싱한다"고 적어 둔 것은 사실을 반대로 말한 것이다.
 *
 * **그래서 조건도 이 파일이 문자열로 만든다.** `apps/web/src/lib/api.ts` 가 질의를 직접
 * 조립하고 있었는데, 그것은 "엔드포인트 문자열은 shared 가 만든다"는 이 파일의 규약이
 * 깨진 자리였다. 앱이 붙을 때 같은 조립을 한 번 더 쓰게 되고, 두 조립이 갈리면 같은 조건이
 * 앱과 웹에서 다른 목록을 낸다. */
export const jobEndpoints = {
  /** 내 작업 목록. `A-10`·`A-44`·`W-20`·`W-55` 가 같은 것을 부른다. */
  list: (query?: JobListQuery) => {
    // URLSearchParams 를 쓰지 않는다. 이 파일은 Workers 와 Hermes 양쪽에서 도는데
    // RN 쪽 구현이 부분적이다(index.ts 머리말의 규약). 손으로 붙이고 값만 인코딩한다.
    const parts: string[] = [];
    const add = (name: string, value: string) =>
      parts.push(`${name}=${encodeURIComponent(value)}`);

    const filter = query?.filter;
    if (filter) {
      // 칩 키를 그대로 보낸다. 서버가 `resolveStatuses` 로 같은 매핑을 쓴다. 여기서
      // 상태값으로 풀어 보내면 칩 하나가 상태 여럿을 가리키는 매핑이 정본이 둘이 된다.
      for (const key of filter.statusKeys) add(JOB_SEARCH_PARAMS.status, key);
      for (const origin of filter.origins) add(JOB_SEARCH_PARAMS.origin, origin);

      const range = resolveDateRange(filter, query?.dateRange);
      if (range.from) add(JOB_SEARCH_PARAMS.from, range.from);
      if (range.to) add(JOB_SEARCH_PARAMS.to, range.to);
    }
    if (query?.limit !== undefined) add(JOB_SEARCH_PARAMS.limit, String(query.limit));
    if (query?.offset !== undefined) add(JOB_SEARCH_PARAMS.offset, String(query.offset));
    // 서버는 정확히 `"1"` 만 켠 것으로 읽는다. `true` 가 아니면 아예 붙이지 않는다.
    if (query?.thumbs === true) add(JOB_SEARCH_PARAMS.thumbs, "1");
    return parts.length ? `/api/jobs?${parts.join("&")}` : "/api/jobs";
  },
  create: () => "/api/jobs",
  uploadImage: (jobId: string) => `/api/jobs/${jobId}/images`,
  process: (jobId: string) => `/api/jobs/${jobId}/process`,
  /** 장 하나만 다시 처리한다 — `A-22` 행의 `↻`, `A-25` 확인 시트가 이것을 부른다.
   *
   * **되살리기는 새 호출이라 우리 원가가 또 나간다. 셀러에게 또 청구하는지는 미결이다**
   * (PRD `S3b` · `13.1` O-10. 선불이라 "이미 낸 돈 안"인지 "추가 청구"인지 안 정해졌다).
   * 어느 쪽이든 공짜로 부를 수 있는 호출이 아니다. 그래서 화면은 바로 부르지 않고
   * `A-25` 를 먼저 띄운다. 서버는 `job-rules.ts` 의 `canRetryImage` 가 true 인 경우를
   * 전부 받아준다 — 이미 `DONE` 인 장은 provider 를 부르지 않고 있는 결과를 그대로 준다. */
  retryImage: (jobId: string, imageId: string) => `/api/jobs/${jobId}/images/${imageId}/process`,
  /** **장 하나를 다시 만든다.** `A-25` 시트의 `다시 만들기` 가 부른다. 본문은
   *  `RemakeImageRequest`, 응답은 `JobResponse`.
   *
   * ⚠️ **위 `retryImage` 와 조건이 정확히 반대다.** 그쪽은 못 만든 장(`FAILED`)을 되살리고
   * **이미 `DONE` 인 장에는 provider 를 부르지 않는다**(있는 결과를 그대로 준다). 이쪽은
   * `DONE` 인 장만 받고 **반드시 새로 불러 재과금한다**(PRD `7.6.8` · `7.13` D-62).
   * 두 라우트를 하나로 합치지 마라 — 합치면 "되살리기"와 "다시 만들기"를 가르는 것이
   * 주소가 아니라 장의 상태가 되고, **상태를 잘못 읽은 요청 하나가 곧 돈이다.**
   *
   * **셀러가 보는 낱말은 둘로 갈렸다**(`7.13` D-62 닫힘, 2026-09-13. 표는 PRD `7.5.9`).
   * 가르는 축은 **공급자를 부르느냐**, 곧 돈이 드느냐다. 업로드 큐 쪽은 부르지 않으므로
   * `다시 올리기`, 이 라우트와 위 되살리기는 둘 다 부르므로 **둘 다 `다시 만들기`** 다.
   * 되살리기와 다시 만들기를 화면에서 가르지 않은 것은, 셀러가 하는 일이 같고 낱말이
   * 셋이 되면 정작 중요한 "돈이 드느냐"가 흐려지기 때문이다. **금액 차이는 낱말이 아니라
   * 금액 줄이 말한다.** `다시 시도` 는 이제 **장이 아닌 재요청**(조회·결제 실패)에만 쓴다.
   * ⛔ **낱말이 같아졌다고 라우트를 합치지 마라.** 바로 위 문단이 그 이유다.
   *
   * ⚠️ **이 줄은 라우트와 한 몸이다**(`approve`·`events` 와 같은 성격).
   * `POST /api/jobs/:jobId/images/:imageId/remake` 를 세우는 브랜치에서 함께 나간다. */
  remakeImage: (jobId: string, imageId: string) => `/api/jobs/${jobId}/images/${imageId}/remake`,
  /** **못 만든 장을 어떻게 할지 고른 것을 서버에 적는다**(`A-24e` 시트의 `seg`.
   *  PRD `7.6.7d` · `docs/DATA_DICTIONARY.md` 34장). 본문은 `DecideImageRecallRequest`,
   *  응답은 `JobResponse`.
   *
   * ⭐ **이 주소는 공급자를 부르지 않는다. 그것이 위 둘과 갈리는 축이다.**
   * `retryImage`·`remakeImage` 는 **부르는** 주소라 한 번 누를 때마다 원가가 나가는데,
   * 이 주소는 **기록만** 한다. 시트가 `어느 쪽을 고르셔도 지금 나가는 돈은 없습니다` 라고
   * 적는 근거가 그것이고, 부르는 것은 그 뒤 `process`(`만들기 시작`)다.
   *
   * ⛔ **`decision: "RECALL"` 을 `retryImage` 로 대신 부르지 마라.** 그 주소는 그 자리에서
   * 공급자를 부르고 **한 장씩 자기 예산을 쓴다**(`singleImageBudget`). 셋을 고른 셀러가
   * `만들기 시작` 한 번에 세 번의 회차를 도는 셈이 되고, **시트의 금액 문장이 그 자리에서
   * 거짓이 된다.**
   *
   * ⚠️ **이 줄은 라우트와 한 몸이다**(`approve`·`remakeImage` 와 같은 성격).
   * `POST /api/jobs/:jobId/images/:imageId/decision` 을 세우는 브랜치에서 함께 나간다. */
  decideImageRecall: (jobId: string, imageId: string) =>
    `/api/jobs/${jobId}/images/${imageId}/decision`,
  /** ⭐ **셀러가 자기 상품이 무엇인지 말한 것을 기록한다**(`A-26c` 시트 · `W-26` 오른쪽
   *  패널. `PUT`. 본문은 `ProductCategoryStatementRequest`, 응답은
   *  `ProductCategoryStatementResponse`. PRD `7.6.9b`).
   *
   * ⛔ **이 주소는 아무것도 막지 않는다. 적기만 한다.** 결제·배경 지우기·내보내기는 이
   * 주소와 아무 관계가 없고, 진술 때문에 서는 것은 **상품명 만들기 하나**다(`A-50b`).
   *
   * ⭐ **`PUT` 인데 덮어쓰지 않는다.** 서버가 하는 일은 **이력 한 줄을 쌓는 것**이라
   * (`JobEvent`) 앞의 진술이 남는다. 셀러가 값을 바꿔도 **「그때 무엇이라고 하셨는지」가
   * 사라지지 않는다** — 그것이 이 칸이 「고르는 칸」이 아니라 **「진술의 기록이 남는 칸」**
   * 인 이유다(법무 `docs/LEGAL_IP_GUIDE.md` `6.2.1`(2)).
   *
   * ⛔ **본문에 문장을 실어 보내도 저장되지 않는다.** 서버가 공유 목록에서 꺼내 적는다 —
   * 셀러가 읽지 않은 말이 셀러 진술로 남으면 그 기록은 증거가 아니다. */
  productCategoryStatement: (jobId: string) => `/api/jobs/${jobId}/product-category-statement`,
  get: (jobId: string) => `/api/jobs/${jobId}`,
  imageOutput: (jobId: string, imageId: string) => `/api/jobs/${jobId}/images/${imageId}/output`,
  /** `A-31` 의 `좋아요`. 결과를 확정해 판을 만든다(`JobVersion`). PRD 7.9 "판은 두 곳에서만
   *  생긴다" 의 앞쪽 지점이고, 그래서 `createdVia` 가 `APP_APPROVAL` 이다.
   *  **본문은 `PublishSelectionRequest`**(생략 가능), 응답은 `ApproveJobResponse` 다.
   *
   * ⚠️ **이 줄은 라우트와 한 몸이다**(`appExchange`·`withdraw` 와 같은 성격).
   * `POST /api/jobs/:jobId/approve` 를 세우는 브랜치에서 함께 나가고, 라우트가 그 브랜치에서
   * 빠지면 이 줄도 함께 빠진다. 이 파일만 머지되면 머리말의 규약이 막으려던 상태(클라이언트가
   * 없는 주소를 부른다)가 그대로 생긴다. */
  approve: (jobId: string) => `/api/jobs/${jobId}/approve`,
  /** **허용 편집 넷 중 상품명·배경을 저장한다**(`PATCH`. 본문은 `UpdateJobRequest`, 응답은
   *  `JobResponse`). `A-50`·`W-30` 의 자동 저장이 이것을 부른다 — 그 화면은 저장 버튼이
   *  없고 값이 바뀔 때마다 부른다(PRD `7.7.4` "자동 저장한다").
   *
   * ⚠️ **`PATCH` 다.** 두 칸 중 바뀐 것만 보내라는 뜻이고, 안 보낸 칸은 건드리지 않는다.
   * `PUT` 으로 읽어 두 칸을 늘 함께 보내면 **다른 기기에서 방금 바꾼 값을 덮는다.**
   *
   * ⚠️ **편집은 판을 만들지 않는다**(PRD `7.9`). 이 호출로 달라지는 것은 작업의 현재
   * 상태와 이력 한 줄뿐이다. 되돌아갈 자리는 승인·내보내기가 만든다. */
  update: (jobId: string) => `/api/jobs/${jobId}`,
  /** **허용 편집 넷 중 블록 켜고 끄기**(`PUT`. 본문은 `UpdateJobBlocksRequest`, 응답은
   *  `JobResponse`). `A-50`·`W-30` 의 토글 다섯 줄이 이것을 부른다.
   *
   * ⚠️ **토글 하나마다 부르지 않고 바뀐 것들을 한 요청에 담는다.** 연속으로 두 개를 끄면
   * 두 요청이 겹치는데, 각자 읽고 각자 쓰면 뒤엣것이 앞엣것을 되살린다. 한 요청이면 그
   * 경합이 없다. */
  blocks: (jobId: string) => `/api/jobs/${jobId}/blocks`,
  /** **허용 편집 넷 중 사진 순서**(`PUT`. 본문은 `ReorderJobImagesRequest`, 응답은
   *  `JobResponse`). `A-51`·`W-30` 의 끌어 옮기기가 이것을 부른다.
   *
   * ⚠️ **그 작업의 사진 전부를 순서대로 담아야 한다.** 일부만 보내면 400 이다. 서버가
   * 나머지를 추측해 채우면 **맨 위가 대표컷이라는 규칙**(`docs/TEMPLATE.md` 5장)이 셀러가
   * 보지 않은 자리에서 정해진다. */
  imageOrder: (jobId: string) => `/api/jobs/${jobId}/images/order`,
  /** **내보내기**(`POST`. 본문은 `PublishSelectionRequest`(생략 가능), 응답은
   *  `ExportJobResponse`). `W-40` 의
   *  `전체 받기 · ZIP` 과 `A-52` 가 부른다. PRD `7.9` "판은 두 곳에서만 생긴다" 의
   *  뒤쪽 지점이고, 그래서 `createdVia` 가 `EXPORT` 다.
   *
   * ⚠️ **이것은 파일을 주지 않는다.** 판을 만들고 작업을 `내보냄` 으로 올릴 뿐이다. 파일은
   * 아래 `versionBundle` 로 따로 받는다. 한 응답에 둘을 담으면 **묶음 내려받기가 실패했을 때
   * 판까지 되돌려야 하는지**가 애매해지고, 셀러는 이미 `내보냄` 이 된 작업을 본다.
   *
   * ⚠️ **`준비됨`·`내보냄` 에서만 받는다**(PRD `7.7.5` — 그 밖에서는 서브탭 자체가 비활성).
   * 그 밖의 상태는 409 `NOT_EXPORTABLE`(처리 중이면 `ALREADY_PROCESSING`, 파기됐으면
   * `JOB_DISCARDED`)이다. */
  export: (jobId: string) => `/api/jobs/${jobId}/export`,
  /** **그 작업의 판 목록**(`GET`. 응답은 `JobVersionListView`). `W-50` 의 `내보낸 판` 과
   *  `W-40` 의 받을 파일 목록이 같은 것을 읽는다.
   *
   * **파일 목록까지 함께 내려간다.** `W-40` 이 파일 이름·크기·용량을 한 번에 그려야 하는데
   * (PRD `7.7.5`), 판 목록과 파일 목록이 두 호출로 갈리면 그 화면이 두 번 기다린다. 판 수는
   * 작업 하나에 많아야 몇 개라 응답이 길어지지 않는다. */
  versions: (jobId: string) => `/api/jobs/${jobId}/versions`,
  /** 판 파일 **하나**. 응답은 그 파일 바이트다(`JobVersionFile.contentType` 그대로).
   *
   * ⚠️ **화면에 파일별 받기 버튼을 만들지 마라.** `W-40` 의 `여기선 아직 못 합니다` 가
   * `파일 하나만 골라 받기` 를 명시적으로 빼 뒀다(PRD `7.7.5`). 이 주소는 목록이 그린 파일을
   * 미리 보거나 묶음이 실패했을 때 쓰는 낱장 경로이고, **셀러가 받는 정식 경로는 아래 묶음
   * 하나다.** */
  versionFile: (jobId: string, versionId: string, fileId: string) =>
    `/api/jobs/${jobId}/versions/${versionId}/files/${fileId}`,
  /** **판 하나를 ZIP 으로 받는다**(`GET`). `W-40` 의 `전체 받기 · ZIP` 과 `W-50` 의
   *  `다시 받기` 가 이것을 부른다. 묶음 형식은 채널이 정한다(`ChannelSpec.bundleFormat`).
   *
   * ⚠️ **`fetch` 로 받아 메모리에 담지 마라.** 응답이 스트림이라 `<a download>` 나
   * `location.assign` 으로 브라우저에 맡기는 것이 맞다. 다만 이 주소는 **세션이 필요하다** —
   * 웹은 쿠키가 실리므로 최상위 이동으로 받을 수 있고, 앱은 Bearer 를 실어야 하므로
   * 파일로 내려받는 네이티브 경로를 쓴다. */
  versionBundle: (jobId: string, versionId: string) =>
    `/api/jobs/${jobId}/versions/${versionId}/bundle`,
  /** `A-12`·`W-50` 타임라인. 그 작업의 `JobEvent` 를 최신순으로 준다(`JobEventListView`).
   *
   * **`DetailJobView.lastActivity` 와 같은 것을 보지 않는다.** 그쪽은 목록 한 줄이 쓰는
   * 마지막 한 건의 사본이고, 이쪽이 이력 정본이다.
   *
   * ⚠️ 위 `approve` 와 같은 브랜치에서 라우트가 함께 선다. 같은 조건으로 함께 빠진다. */
  events: (jobId: string) => `/api/jobs/${jobId}/events`,
} as const;

/** 결제 이력(`A-43`·`W-70`)과 정산 명세(`A-43b`). **작업이 아니라 셀러에 매달린 자원이라
 * `/api/jobs` 밑에 두지 않는다.**
 *
 * **정본은 `Payment` + `BillingItem` 행이다**(`docs/DATA_DICTIONARY.md` 15장).
 * ⛔ 이 두 주소가 `JobEvent` 를 읽어 금액을 만들면 안 된다. 그 칸은 표시용 사본이고,
 * **공급자를 부르기 전에** 적혀서 실패한 호출에도 금액이 남는다(15.2).
 *
 * ⚠️ **두 줄 다 라우트와 한 몸이다**(`approve`·`events`·`withdraw` 와 같은 성격).
 * **지금 `apps/api` 에 결제 라우트가 하나도 없다.** `GET /api/payments` 와
 * `GET /api/payments/:paymentId` 를 세우는 브랜치에서 함께 나가고, 라우트가 그 브랜치에서
 * 빠지면 이 줄들도 함께 빠진다. 이 파일만 머지되면 머리말의 규약이 막으려던 상태
 * (클라이언트가 없는 주소를 부른다)가 그대로 생긴다. */
export const paymentEndpoints = {
  /** 내 결제 이력. **응답은 `PaymentHistoryView` 이고 달로 묶여서 온다.**
   *
   * **시간대를 항상 실어 보낸다.** 빼면 서버가 UTC 로 묶어 월말 밤 결제가 지난 달 머리글
   * 밑으로 들어간다. 기본값을 기기 시간대로 둔 것이 그래서다. 앱·웹은 아무것도 안 넘겨도
   * 옳고, 서버가 서버를 부를 때만 `dateRange.offsetMinutes` 를 명시한다. */
  history: (query?: PaymentHistoryQuery) => {
    // URLSearchParams 를 쓰지 않는 이유는 `jobEndpoints.list` 와 같다(Hermes).
    const parts: string[] = [];
    const add = (name: string, value: string) =>
      parts.push(`${name}=${encodeURIComponent(value)}`);

    const filter = query?.filter;
    // `ALL` 도 적는다. 기본값을 생략하면 서버와 클라이언트가 각자 기본값을 들고 있게 되고,
    // 한쪽이 바뀌는 날 조건 없는 요청이 다른 목록을 받는다.
    if (filter) {
      add(PAYMENT_HISTORY_PARAMS.scope, filter.scope);
      if (filter.year !== null) add(PAYMENT_HISTORY_PARAMS.year, String(filter.year));
    }

    const offset = query?.dateRange?.offsetMinutes ?? deviceUtcOffsetMinutes(query?.dateRange?.now);
    add(PAYMENT_HISTORY_PARAMS.offset, String(offset));

    return `/api/payments?${parts.join("&")}`;
  },
  /** 정산 명세 한 건(`A-43b`). 응답은 `RefundStatementView`.
   *
   * **`jobId` 가 아니라 `paymentId` 로 연다.** 한 작업에 결제가 여럿 생길 수 있고
   * (실패·취소분이 남는다. `0002` 의 `Payment_jobId_active_uq`), 명세는 **거래 한 건**의
   * 문서다. 이력 줄이 이미 `paymentId` 를 들고 있어 화면이 새로 찾을 것도 없다.
   *
   * ⭐ **그 인덱스가 `0007` 에서 `Payment_jobId_open_uq` 로 바뀐다**(파일은 있고 **아직
   * 적용 전이다**. `docs/DATA_DICTIONARY.md` 17장). 조건이 **"살아 있는 결제"에서 "아직
   * 끝나지 않은 결제"로** 좁아지고, 그 뒤로는 **매입이 끝난 결제까지 여러 건 남는다**
   * (다시 만들기가 별도 카드 거래라서 그래야 한다). **이 문단의 결론은 그대로이고 더
   * 강해진다.** 결제가 여럿일 수 있는 이유가 실패·취소분 말고 하나 더 생긴다. */
  statement: (paymentId: string) => `/api/payments/${paymentId}`,
  /** 작업 한 건의 청구. 응답은 `JobBillingView` 이고 **살아 있는 결제 id 가 그 안에 있다.**
   *
   * **작업 화면에서 명세로 가는 길이 이 줄이다.** `A-31`·`A-32`·`W-30` 은 `jobId` 만 들고
   * 있는데 명세는 `paymentId` 로만 열린다. 그 짝을 서버가 찾아 준다.
   *
   * ⚠️ **앱과 웹이 청구 내역 목록에서 그 짝을 찾고 있었다**(2026-09-14 이중 코드리뷰).
   * 그 응답은 `PAYMENT_HISTORY_MAX_ROWS` 에서 잘리므로 **줄이 500 을 넘은 셀러는 오래된
   * 작업의 명세를 영영 열지 못하고, 화면은 "기록을 찾지 못했습니다"라고 거짓말했다.**
   * 상한을 쪽 나누기 대신으로 정한 결정과 그 조회가 서로를 모르고 있던 자리다.
   *
   * **`/api/payments` 밑에 둔다.** 금액을 말하는 주소를 한 파일에 모아 두는 것이 `⛔ 원가를
   * 응답에 싣지 않는다` 를 지키는 자리를 하나로 만든다(`routes/payments.ts` 머리말).
   * 세 마디(`/api/payments/by-job/:jobId`)라 두 마디인 `statement` 와 겹치지 않는다. */
  forJob: (jobId: string) => `/api/payments/by-job/${jobId}`,
  /** **결제를 시작하기 전에 서버에 금액을 물어본다.** 응답은 `PaymentCheckoutView`.
   *
   * ⭐ **화면이 금액을 만들지 않는다.** 위젯에 넣을 `amount`·`orderId` 를 서버가 정해 주고,
   * 승인 단계(`confirm`)에서 서버가 **그 수를 다시 만들어 대조한다.** 화면이 계산한 값을
   * 그대로 승인에 쓰면 브라우저에서 고친 금액이 그대로 카드에 청구된다.
   *
   * **저장하지 않는다.** 이 주소는 읽기다(그래서 `GET` 이다). 결제 행은 승인이 실제로 선
   * 뒤에 생긴다(`lib/billing.ts` 의 "결제를 미리 만들지 않는 것도 규약이다"). */
  checkout: (jobId: string) => `/api/payments/checkout/${jobId}`,
  /** **승인 확정.** 위젯이 돌려준 `paymentKey`·`orderId`·`amount` 를 서버가 받아 PG 에
   *  승인을 확정한다. 본문은 `ConfirmPaymentRequest`, 응답은 `ConfirmPaymentResult`.
   *
   * ⛔ **`jobId` 를 본문에 싣지 않는다.** 작업은 `orderId` 에서 되읽는다(서버가 만든
   * 값이다). 따로 받으면 **남의 작업의 `paymentKey` 를 내 작업에 붙이는 요청**이 문법적으로
   * 가능해지고, 그것을 막는 일이 대조 한 줄에 매달린다. */
  confirm: () => `/api/payments/confirm`,
  /** **결제됨 · 아직 시작 전의 전액 취소**(`A-28`·`W-26` 의 `취소하기`. `POST`). 응답은
   *  `CancelBeforeStartResult`, 실패 코드는 `ApiErrorCode` 의 「시작 전 취소」 다섯 +
   *  `PAYMENT_NOT_CONFIGURED`.
   *
   * ⛔ **본문을 싣지 않는다. 금액도 보내지 않는다.** 돌려줄 금액은 서버가 그 작업의 결제
   * 행(`authorizedAmount`)에서 정한다. 실어도 서버가 읽지 않지만, 보내는 칸이 있으면 언젠가
   * 누가 그것을 믿는다.
   *
   * **`paymentId` 가 아니라 `jobId` 로 부른다.** 셀러가 누르는 자리(`A-28`)는 작업 화면이고
   * 결제 id 를 들고 있지 않다. 어느 결제를 취소할지는 서버가 고른다(`lib/payment-cancel.ts`
   * 의 `readCancelablePayment`). 세 마디라 두 마디인 `statement` 와 겹치지 않는다. */
  cancel: (jobId: string) => `/api/payments/cancel/${jobId}`,
  /** **결제위젯 실패 보고**(웹 전용. `POST`, 인증 필요). 본문은 `PaymentClientErrorRequest`,
   *  응답은 **204 · 본문 없음**.
   *
   * 서버는 Workers Logs 에 `warn` 한 줄만 남긴다. ⛔ **D1 에 아무것도 적지 않는다.** 결제 행도,
   * 작업 이벤트도 만들지 않는다. 결제위젯 실패는 우리 서버를 거치지 않고 브라우저 안에서 끝나서,
   * 이 보고가 없으면 「결제를 시도했는데 왜 안 됐는가」가 어디에도 남지 않는다.
   *
   * ⚠️ 라우트와 한 몸이다(위 줄들과 같은 성격). 라우트가 빠지면 이 줄도 함께 빠진다. */
  clientError: () => `/api/payments/client-error`,
} as const;

/** `POST /api/payments/cancel/:jobId` 의 성공 응답.
 *
 * ⚠️ **`reused: true` 는 「이번 요청이 PG 를 부르지 않았다」만 뜻하지 않는다.** 앞선 취소가
 * 결과를 장부에 못 적은 채 끝났고(미확정 표지) 이번 요청이 PG 조회로 그것을 맞춘 경우도
 * `true` 다. 어느 쪽이든 **화면이 할 일은 성공과 같다.** 이 칸은 로그·검증용이다. */
export interface CancelBeforeStartResult {
  jobId: string;
  paymentId: string;
  /** 돌아간 금액. **서버가 결제 행에서 정한 값이다.** 요청 본문에서 오지 않는다. */
  canceledAmount: number;
  /** 이미 취소돼 있던 것을 돌려줬다(두 번째 요청 · 표지 맞춤). */
  reused: boolean;
}

/** `GET /api/payments/checkout/:jobId`. **결제위젯을 띄우는 데 필요한 것 전부.** */
export interface PaymentCheckoutView {
  jobId: string;
  /** 서버가 만든 주문번호. **화면이 만들지 않는다.** */
  orderId: string;
  /** 청구할 장수. 담긴 장 중 아직 배경을 안 지운 것. */
  quantity: number;
  /** 장당 단가. 화면이 명세 줄을 그리는 데 쓴다. */
  unitPriceAmount: number;
  /** 합계. **승인 단계에서 서버가 이 수를 다시 만들어 대조한다.** */
  amount: number;
  /** 결제위젯 `clientKey`. **브라우저에 나가는 공개 값이다**(`env.ts` 의 `TOSS_CLIENT_KEY`).
   *  ⛔ 웹 소스에 값을 박지 말고 이 칸을 쓴다. 박으면 테스트 키와 라이브 키가 섞인 상태가
   *  프런트엔드 재배포 없이는 안 풀린다. */
  clientKey: string;
}

/** `POST /api/payments/confirm` 의 본문. **위젯이 돌려준 것을 그대로 옮긴다.** */
export interface ConfirmPaymentRequest {
  paymentKey: string;
  orderId: string;
  /** ⚠️ **서버는 이 값을 믿지 않고 대조에만 쓴다.** 서버가 다시 센 금액과 다르면 409
   *  `PAYMENT_AMOUNT_MISMATCH` 이고 **PG 를 부르지 않는다.** */
  amount: number;
}

export interface ConfirmPaymentResult {
  paymentId: string;
  jobId: string;
  /** 실제로 승인된 금액. **PG 가 답한 값이다.** */
  amount: number;
  /** 표시용 결제 수단 요약(`카드 1234`). 없을 수 있다. */
  methodLabel: string | null;
  /** **이번에 PG 를 안 불렀다.** 이미 승인돼 있던 것을 그대로 돌려줬다(응답 유실 재시도).
   *  화면이 할 일은 성공과 같다. 이 칸은 로그·검증용이다. */
  reused: boolean;
}

// ── 결제위젯 실패 보고 (`POST /api/payments/client-error`) ─────────────────────────────
//
// 웹이 토스 결제위젯에서 실패를 만나면 **어느 단계에서 · 어떤 코드로** 실패했는지만 보낸다.
// 서버는 `warn` 한 줄을 남기고 204 로 답한다. D1 에 저장하지 않는다(마이그레이션 0건).
//
// ⛔ **토스 `message` · `clientKey` · URL 은 보내지 않는다. DTO 에 칸이 없는 것이 그 규약이다.**
//   - `message`: 토스가 만든 자유 문장이다. 형식을 우리가 통제하지 못하고, 카드사 거절 사유처럼
//     결제 수단에 관한 문장이 섞일 수 있다. 로그는 Workers Logs 에 남고 거기서 지울 수 없으므로
//     **모양을 아는 값만** 남긴다. 원인 분류는 `code` 로 충분하다.
//   - `clientKey`: 공개 값이지만 서버가 이미 안다(`PaymentCheckoutView.clientKey`). 받으면
//     테스트 키·라이브 키 구분을 **브라우저가 말한 값**으로 하게 된다.
//   - URL: `failUrl` 로 돌아온 주소에는 토스가 붙인 쿼리(`message` 포함)가 통째로 실려 있어,
//     URL 을 받으면 위 `message` 를 뒷문으로 받는 셈이다. 단계는 `stage` 가, 원인은 `code` 가 말한다.
//   칸이 있으면 언젠가 누가 채운다(`cancel` 본문을 싣지 않는 이유와 같다). 넓혀야 하면 칸을 더하기
//   전에 `error-log-manager`(`docs/ERROR_LOGGING.md`) 검토를 받는다.

/** 결제위젯이 실패할 수 있는 단계. **순서가 곧 위젯을 띄우는 순서다.**
 *
 * | 값 | 그 단계 |
 * |---|---|
 * | `load` | SDK 스크립트 적재 |
 * | `widgets` | `TossPayments(clientKey).widgets()` |
 * | `amount` | `setAmount` |
 * | `render` | `renderPaymentMethods` · `renderAgreement` |
 * | `request` | `requestPayment` |
 * | `return` | 결제창이 `failUrl` 로 되돌려 보낸 `code` |
 *
 * ⛔ 이 값을 셀러 화면에 내지 않는다. 내부 분류다(`docs/PRD.md` 7절 용어 규칙). */
export const PAYMENT_WIDGET_STAGES = [
  "load",
  "widgets",
  "amount",
  "render",
  "request",
  "return",
] as const;

export type PaymentWidgetStage = (typeof PAYMENT_WIDGET_STAGES)[number];

export function isPaymentWidgetStage(v: unknown): v is PaymentWidgetStage {
  return typeof v === "string" && (PAYMENT_WIDGET_STAGES as readonly string[]).includes(v);
}

/** 보고에 실을 수 있는 `code` 의 모양. 토스 오류 코드(`PAY_PROCESS_CANCELED` 같은)가 이 모양이다.
 *
 * ⭐ **웹과 API 가 같은 규칙으로 거른다.** 웹은 보내기 전에, API 는 받은 뒤에 다시 거른다. 서버가
 * 브라우저를 믿지 않는 것이 이유이고, 한쪽만 거르면 그쪽이 바뀌는 날 로그에 자유 문장이 들어간다.
 * 길이 상한 64 는 로그 한 줄을 부풀리지 못하게 하는 값이다. */
export const PAYMENT_CLIENT_ERROR_CODE_PATTERN = /^[A-Z][A-Z_]{0,63}$/;

/** 모양이 맞지 않는 코드를 대신하는 값. **버리지 않고 이 값으로 바꾼다.** 실패가 있었다는 사실과
 *  단계는 남겨야 한다. */
export const PAYMENT_CLIENT_ERROR_UNKNOWN = "UNKNOWN";

/** 받은 값이 문자열이고 패턴에 맞으면 그대로, 아니면 `PAYMENT_CLIENT_ERROR_UNKNOWN`.
 *  ⛔ 다듬지 않는다(대문자로 바꾸기·자르기 없음). 고쳐서 맞추면 원래 무엇이었는지 로그가 거짓말한다. */
export function paymentClientErrorCode(raw: unknown): string {
  return typeof raw === "string" && PAYMENT_CLIENT_ERROR_CODE_PATTERN.test(raw)
    ? raw
    : PAYMENT_CLIENT_ERROR_UNKNOWN;
}

/** `POST /api/payments/client-error` 의 본문. **세 칸이 전부다.** 위 머리말의 ⛔ 를 보라. */
export interface PaymentClientErrorRequest {
  /** 결제하려던 작업. ⚠️ **브라우저가 말한 값이다.** 라우트가 로그인한 셀러의 작업인지 확인하지
   *  않았다면, 로그를 읽는 사람이 이 값을 그 셀러의 작업으로 믿으면 안 된다. */
  jobId: string;
  stage: PaymentWidgetStage;
  /** `paymentClientErrorCode()` 를 거친 값. 서버도 다시 거른다. */
  code: string;
}

/** `POST /process` 와 `GET /api/jobs/:jobId` 는 같은 형태를 돌려준다. */
export type JobResponse = DetailJobView;

/** `POST /api/jobs/:jobId/images/:imageId/remake` 의 본문.
 *
 * **이 한 칸이 이 기능의 이중 과금 방지 장치 전부다.** 새 컬럼도 멱등 키 테이블도 두지
 * 않았고(`docs/DATA_DICTIONARY.md` 12장), 대신 **클라이언트가 본 호출 횟수를 그대로 돌려
 * 보내게 해서 서버가 비교-후-증가(compare-and-swap)로 막는다.**
 *
 * **왜 이것으로 충분한가**: 네트워크가 응답을 흘려 클라이언트가 같은 요청을 다시 보내도,
 * 그때 `JobImage.providerCallCount` 는 이미 올라가 있어 두 번째 요청은 어긋나고 409
 * (`STALE_CALL_COUNT`)로 거절된다. 멱등 키를 두면 "같은 결과를 다시 돌려준다"까지 되지만,
 * **끊긴 요청 뒤 클라이언트가 할 일은 어차피 작업을 다시 읽는 것**이라 그 차이가 화면에
 * 남지 않는다. 값을 하나 더 저장할 이유가 되지 못한다.
 *
 * ⚠️ **어느 쪽도 provider 쪽 이중 과금을 막아 주지는 않는다.** 호출이 나간 뒤 응답이
 * 유실되면 Photoroom 은 이미 세었고 우리는 그것을 되물을 방법이 없다. 이 값이 막는 것은
 * **우리가 같은 요청을 두 번 내보내는 것**까지다. 그 이상을 이 필드에 기대하지 마라. */
export interface RemakeImageRequest {
  /** 클라이언트가 화면에서 본 그 장의 `JobImageView.providerCallCount` **그대로**.
   *
   * ⛔ **더하거나 빼지 마라.** `+1` 을 보내면 서버 비교가 영원히 어긋나고, 시트를 띄울 때
   * 세어 둔 값을 다시 만들기 성공 뒤에도 재사용하면 두 번째 누르기가 통과한다. 값을 만드는
   * 자리를 하나로 두려고 `remakeRequestFor()` 를 함께 둔다.
   *
   * 이름이 `providerCallCount` 를 그대로 달고 있는 것은 **어느 칸에서 베껴야 하는지를
   * 이름이 말하게** 하려는 것이다(`fromCallCount` 라는 칸은 어디에도 없다). */
  fromProviderCallCount: number;
}

/** 다시 만들기 본문을 **화면이 손으로 만들지 않게** 한다. 지금 보고 있는 장을 그대로 넘긴다.
 *
 * 함수 하나짜리라 없어도 되는 것처럼 보이지만, 없으면 앱과 웹이 각자 `image.providerCallCount`
 * 를 찾아 적고 **그 자리가 곧 장당 과금이다.** `A-25` 시트가 숫자를 들고 있다가 눌릴 때
 * 보내는 구조라면, **시트를 열 때가 아니라 보낼 때의 장**으로 부르는 것이 맞다. */
export function remakeRequestFor(image: JobImageView): RemakeImageRequest {
  return { fromProviderCallCount: image.providerCallCount };
}

/** `POST /api/jobs/:jobId/approve` 와 `POST /api/jobs/:jobId/export` 의 **본문**.
 *  ⭐ **셀러가 이번 판에 넣기로 확인·확정한 장**이다(2026-09-24 사용자 확정. PRD `13.1`
 *  `O-73`. 법무 `L-F44`(4)·`G1-27`-⑭ⓑ).
 *
 * 지켜야 하는 성질은 하나다: **`amount > 0` 인 모든 `BillingItem.imageId` 는 셀러가 그
 * 작업에서 받기로 특정한 최종 집합 안에 있어야 한다.** 청구는 **판이 공개될 때** 서므로
 * (`7.12` 20번 ⓐ) 그 집합을 받는 자리가 **PG 결제 화면 앞이 아니라 이 두 주소**다.
 *
 * ⛔ **「담은 장 빼기」가 아니다.** 고르지 않은 장은 지워지지 않고 남으며, 다음 승인·
 * 내보내기에서 다시 고를 수 있다.
 *
 * ## 셋의 뜻이 다르다
 *
 * - **칸 자체를 안 보냄**(`undefined`. 본문 없는 `POST` 포함) — 오늘 경로다. 만들어진 장을
 *   전부 얼린다. ⚠️ **이 갈래가 살아 있는 동안 위 불변식은 서지 않는다.** 확인·확정 화면이
 *   서면 두 호출부가 이 칸을 보내기 시작하고 그때 닫힌다.
 * - **`null` 또는 `[]`** — 409 `NOT_CONFIRMED`. 보내 놓고 하나도 안 골랐다는 뜻이라
 *   **판을 만들지 않는다.**
 * - **id 목록** — 그중 **만들어졌고 실제로 받을 수 있는 장**만 판에 들어가고 청구된다.
 *   모르는 id·못 만든 장의 id 는 조용히 빠진다(그 사이 파기된 장 하나가 나머지 전부를
 *   못 받게 만들면 안 된다). 하나도 안 남으면 409 `NO_RESULT` 다. */
export interface PublishSelectionRequest {
  confirmedImageIds?: string[] | null;
}

/** `POST /api/jobs/:jobId/images/:imageId/decision` 의 본문(`A-24e`. PRD `7.6.7d` ·
 *  `docs/DATA_DICTIONARY.md` 34장).
 *
 * ⛔ **`RemakeImageRequest` 의 `fromProviderCallCount` 같은 칸을 두지 않았다. 둘 필요가
 * 없어서다.** 그 칸이 막는 것은 **같은 요청이 두 번 나가 공급자를 두 번 부르는 것**인데,
 * 이 주소는 **공급자를 부르지 않는다.** 두 번 와도 서버의 한 문장이 흡수한다 —
 * `DECLINE` 은 `recallDeclinedAt IS NULL` 일 때만 찍고, `RECALL` 은 `status = 'FAILED'`
 * 일 때만 되돌린다. **둘째 요청은 0행을 바꾸고 이력 줄도 안 쌓는다**(32.4).
 *
 * ⚠️ **`decision` 을 옵셔널로 두지 않았다.** 빈 본문을 받아주면 **아무 선택도 아닌 것이
 * 요청으로 나가고**, 시트의 `기본 선택 없음`(PRD `7.6.7d`)이 서버에서 무너진다. 안 고른
 * 장은 **요청을 보내지 않는 것**으로 표현한다. */
export interface DecideImageRecallRequest {
  /** ⛔ **화면 낱말(`다시 만들기`·`만들지 않기`)을 그대로 보내지 마라.** 그 낱말은
   *  `design-manager` 소관이라 바뀔 수 있고, 바뀌는 날 배포가 갈린 앱이 400 을 받는다. */
  decision: RecallDecision;
}

/** `POST /api/jobs/:jobId/approve` 의 응답.
 *
 * **승인은 작업 상태를 바꾸지 않는다.** `job.status` 는 승인 전과 같은 값으로 돌아온다 —
 * 승인으로 달라지는 것은 판이 하나 생겼다는 사실이지 작업이 다음 단계로 갔다는 것이 아니다
 * (`EXPORTED` 는 내보냄이고 승인이 아니다). 화면이 `created` 와 `version` 을 읽어야 하는
 * 이유가 이것이다. */
export interface ApproveJobResponse {
  /** 승인 뒤의 작업 전체 상태. `/process` 와 같은 형태를 돌려주는 규약을 따른다. */
  job: DetailJobView;
  /** 이번 승인이 가리키는 판. */
  version: JobVersionView;
  /** 이번 요청이 판을 **새로 만들었는가**. `false` = 마지막 판과 동결 스냅샷이 같아 새 판을
   *  만들지 않고 그 판을 그대로 돌려줬다(응답 유실 재시도·두 번 누르기).
   *
   * **`false` 를 실패로 읽지 않는다.** 두 경우 모두 성공이고 `version` 이 채워져 있다.
   * 이 값이 가르는 것은 화면 문구뿐이다 — 새 판이면 `v3 을 만들었어요`, 아니면 이미 있던
   * 판을 가리킨다. */
  created: boolean;
}

// ── 허용 편집 넷 ───────────────────────────────────────────────────────────
//
// **넷이 세 주소로 갈려 있다** — 상품명·배경이 한 주소(`update`), 블록이 하나(`blocks`),
// 사진 순서가 하나(`imageOrder`)다. 하나로 합치지 않은 이유는 **바뀌는 것의 모양이 다르기
// 때문**이다. 앞은 값 두 개, 가운데는 목록의 부분 갱신, 뒤는 전체 목록의 순서다. 한 본문에
// 담으면 "안 보낸 것은 무엇인가"의 답이 칸마다 달라진다(건드리지 않음 / 끔 / 맨 뒤로).

/** `PATCH /api/jobs/:jobId` 의 본문. **둘 다 옵셔널이지만 하나도 없으면 400 이다** —
 *  빈 본문을 200 으로 받아주면 화면은 저장됐다고 적고 서버는 아무것도 안 한다.
 *
 * ⚠️ **`undefined` 와 `null` 이 다른 뜻이다.** `productName` 이 없으면(`undefined`) 그 칸을
 * 건드리지 않고, `null` 이면 상품명을 비운다. 빈 문자열은 `null` 과 같게 다룬다 — 상품명이
 * 없는 것과 빈 문자열이 두 값이면 목록 한 줄이 둘을 다르게 그린다. */
export interface UpdateJobRequest {
  /** 앞뒤 공백은 서버가 다듬는다. 빈 문자열은 `null` 로 저장된다. */
  productName?: string | null;
  /** **팔레트 안의 값이어야 한다**(`ALLOWED_BACKGROUND_HEXES`). 밖이면 422 다.
   *  대소문자는 서버가 맞춰 저장한다(`normalizeBackgroundColor`). */
  backgroundColor?: string;
}

/** 상품명 길이의 **방어적 절대 상한**. 채널 상한(`ChannelSpec.productNameMaxLength`)이
 *  정해지면 그쪽이 이긴다.
 *
 * ⚠️ **이 값은 스마트스토어 규격이 아니다.** 지금 채널 상한이 `null`(미확인)이라
 * `docs/NAVER_API_CATALOG.md` 가 채워지기 전까지 **아무 길이나 받게 되는 것**을 막는
 * 자리다. 상한이 없으면 한 요청이 수 MB 짜리 문자열을 `DetailJob` 과 판마다의 동결
 * 스냅샷에 눌러 담고, 그 값은 이력·목록·판 응답에 계속 실려 나간다.
 *
 * 100 이나 40 처럼 "그럴듯한" 수를 고르지 않은 이유: 그러면 **미확인 상한을 확정된 것처럼
 * 보이게** 하고, 실제 스마트스토어 상한이 그보다 길면 셀러가 쓸 수 있는 이름을 우리가
 * 막는다. 이 값은 방어선이지 규격이 아니다. */
export const PRODUCT_NAME_HARD_MAX_LENGTH = 200;

/** `PUT /api/jobs/:jobId/blocks` 의 본문. 바뀐 토글만 담아도 되고 다섯 개를 다 담아도 된다.
 *
 * ⚠️ **채널에 없는 블록은 422 로 거절된다**(`channelHasBlock`). 셀러가 끈 블록과 채널에
 * 애초에 없는 블록은 다른 것이라, 없는 블록을 조용히 무시하면 화면은 껐다고 그리고 서버에는
 * 아무 일도 없다. */
export interface UpdateJobBlocksRequest {
  blocks: { blockType: string; enabled: boolean }[];
}

/** `PUT /api/jobs/:jobId/images/order` 의 본문.
 *
 * ⛔ **부분 배열을 보내지 마라.** 그 작업의 사진 전부를 빠짐없이, 중복 없이 담는다. 서버가
 * 400 으로 거절한다 — 재정렬은 순번을 통째로 다시 매기는 일이라, 빠진 장이 있으면 그 장이
 * 남의 자리를 쥔 채 남는다. */
export interface ReorderJobImagesRequest {
  /** 새 순서대로. **0번이 대표컷이다**(`docs/TEMPLATE.md` 5장). */
  imageIds: string[];
}

/** `POST /api/jobs/:jobId/export` 의 응답. **형태가 `ApproveJobResponse` 와 같다** — 같은
 *  일(판 만들기)을 하는 두 지점이라 화면이 같은 코드로 읽는다.
 *
 * `created` 가 `false` 인 경우가 승인 쪽보다 흔하다. 승인한 뒤 아무것도 고치지 않고
 * 내보내면 **마지막 판과 상태가 같아 새 판을 만들지 않는다.** 그때도 이 요청은 성공이고,
 * 작업은 `내보냄` 이 되며 `version` 은 받을 파일이 들어 있는 그 판을 가리킨다.
 * ⛔ **`created: false` 를 실패로 읽지 마라.** */
export interface ExportJobResponse {
  job: DetailJobView;
  version: JobVersionView;
  created: boolean;
}

/** 판 한 줄 + 그 판의 파일 목록. **두 도메인 뷰를 붙인 것이지 새 축이 아니다.**
 *
 * `editStateJson` 은 여기 없다. 화면이 쓰지 않고, 상품명·스펙이 통째로 실려 나가는 자리를
 * 이유 없이 늘리지 않는다(`routes/jobs.ts` 의 `toVersionView`). */
export interface JobVersionWithFilesView extends JobVersionView {
  files: JobVersionFileView[];
}

/** `GET /api/jobs/:jobId/versions` 의 응답. **번호가 큰 것이 위다**(최신 판이 먼저).
 *
 * 배열을 그대로 내리지 않는 이유는 `JobEventListView` 와 같다. */
export interface JobVersionListView {
  items: JobVersionWithFilesView[];
}

/** `GET /api/jobs/:jobId/events` 의 응답. 최신순이다.
 *
 * 배열을 그대로 내리지 않고 한 겹 감싼 이유는 나중에 쪽 나누기(`nextOffset` 같은)가 붙을 때
 * 응답 모양이 바뀌지 않게 하기 위해서다. `JobListView` 가 같은 형태다. */
export interface JobEventListView {
  items: JobEventView[];
}

/** 로그인 엔드포인트. **`apps/api/src/routes/auth.ts` 에 실제로 서 있는 것만 적었다.**
 * 예외는 아래 `appExchange` · `withdraw` · `reviewLogin` 셋이고(`reviewLogin` 은
 * `routes/review-login.ts` 에 선다), 세 줄의 주석이 각각 조건(라우트와 같은 브랜치에서 함께
 * 나간다)을 들고 있다.
 *
 * 이 자리가 생긴 경위: 로그인 라우트가 없던 동안에는 위 규약대로 아무것도 적지 않았고
 * (`packages/shared/src/auth.ts` 머리말), 라우트가 생긴 2026-09-09 에 다섯 줄이
 * `apps/web/src/lib/api.ts` 안에 문자열로 먼저 자리 잡았다. **웹에만 두면 앱이 붙을 때
 * 같은 문자열이 한 벌 더 적히고, 두 벌이 갈리는 순간 한쪽이 없는 주소를 부른다.**
 * `jobEndpoints` 를 shared 에 둔 것과 같은 이유다.
 *
 * **콜백(`/api/auth/:provider/callback`)은 여기 없다.** 그 주소를 부르는 것은 우리
 * 클라이언트가 아니라 공급자이고, 문자열을 만드는 곳은 서버 자신이다
 * (`routes/auth.ts` 의 `callbackUrl`). 클라이언트가 쓰지 않는 경로를 클라이언트 계약에
 * 적으면 누군가 부른다.
 *
 * ⚠️ **`start` 만 `fetch` 가 아니다.** 공급자 화면으로 나갔다 돌아와야 하므로 최상위
 * 이동(`<a href>` · `location.assign`)으로 건다. `fetch` 로 부르면 302 를 따라가서
 * 네이버 로그인 화면 HTML 이 응답 본문으로 돌아온다. */
/** 알림(`A-30` · `W-60`). 행은 `Notification`(0004)이고 DTO 는 `domain.ts` 의
 *  `NotificationView` · `NotificationListView` · `NotificationReadResponse` ·
 *  `NotificationReadAllResponse` 다. 셋 다 미인증이면 401.
 *
 * `readAll` 과 `read(id)` 는 마디 수가 달라(`/read-all` 과 `/:id/read`) 라우트 순서와 무관하게
 * 겹치지 않는다. ⛔ `read-all` 을 `/:id` 꼴로 줄이지 마라. 그 순간 `:id` 가 삼킨다. */
export const notificationEndpoints = {
  /** 목록 + 안 읽음 수. */
  list: () => "/api/notifications",
  /** 단건 읽음. `POST`, 본문 없음. */
  read: (id: string) => `/api/notifications/${encodeURIComponent(id)}/read`,
  /** 모두 읽음. `POST`, 본문 없음. */
  readAll: () => "/api/notifications/read-all",
};

export const authEndpoints = {
  /** 켤 때 한 번 묻는 "나 누구인가". **미인증도 200 이고 `ANONYMOUS_SESSION` 이 온다.** */
  session: () => "/api/auth/session",
  /** `A-48`·`W-70` 의 `내 계정` 두 줄과 `A-40` 가입 시각(`AccountProfile`). 미인증이면 401. */
  account: () => "/api/auth/account",
  /** **이 계정의 세션 전부가 끊긴다.** 앱에서 로그아웃하면 웹도 끊긴다(PRD `7.6.23`). */
  logout: () => "/api/auth/logout",
  /** `A-48b` 탈퇴. 본문 없이 `POST` 하고 `logout` 과 같은 `{ ok: true }` 를 받는다 — 같은
   *  형태라 응답 타입을 따로 두지 않는다.
   *
   * **미인증이면 401 이다. `logout` 과 갈리는 자리가 여기다.** 로그아웃은 이미 끊긴 요청도
   * 200 으로 받아주지만, 탈퇴는 누구를 탈퇴시킬지 모르면 할 일이 없다. 그래서 이 호출만은
   * 401 을 "이미 됐다"로 읽으면 안 된다.
   *
   * **이 뒤로 그 셀러의 세션이 전부 죽는다**(`logout` 과 같은 범위다. 앱에서 탈퇴하면 웹도
   * 끊긴다). 응답을 받은 클라이언트는 로그아웃과 같은 기기 청소를 그대로 돈다.
   *
   * ⚠️ **`appExchange` 와 같은 성격의 줄이다 — 라우트와 한 몸이다.** `POST /api/auth/withdraw`
   * 를 세우는 브랜치에서 함께 나간다. **라우트가 그 브랜치에서 빠지면 이 줄도 함께 빠진다.**
   * 이 파일만 머지되면 머리말의 규약이 막으려던 상태(클라이언트가 없는 주소를 부른다)가
   * 그대로 생긴다. */
  withdraw: () => "/api/auth/withdraw",
  /** 인가 시작. 주소의 공급자 부분은 소문자다. 라우트가 `toUpperCase()` 로 되돌려 **출처별로
   *  두 번 거른다**: 먼저 `WEB_ENABLED_IDENTITY_PROVIDERS`(라우트가 열리는 목록), 앱 시작
   *  (`source=APP`)이면 `ENABLED_IDENTITY_PROVIDERS`(앱 목록)를 한 번 더. **어느 쪽에서
   *  걸려도 404 다.** 콜백도 시도 행의 `source` 로 같은 검사를 다시 한다
   *  (`apps/api/src/routes/auth.ts` 의 `enabledProvider` · `enabledForSource`). */
  start: (provider: IdentityProvider) => `/api/auth/${provider.toLowerCase()}/start`,
  /** 앱만 부른다. 딥링크로 받은 일회용 코드를 세션 토큰으로 바꾼다(`auth.ts` 의 앱 복귀 절.
   *  본문은 `AppExchangeRequest`, 응답은 `AppExchangeResponse`).
   *
   * **이 줄은 라우트와 한 몸이다.** `feat/mobile-login` 이 `POST /api/auth/app/exchange` 를
   * 세우면서 붙었다. 위 규약("실제로 구현된 것만 적는다")을 앞질러 적은 것이 아니라, 앱과
   * 서버가 같은 문자열을 각자 적는 것을 막은 것이다. 웹의 로그인 경로가
   * `apps/web/src/lib/api.ts` 에 먼저 적혀 두 벌이 될 뻔한 자리가 바로 그것이었다.
   * ⚠️ **라우트가 이 브랜치에서 빠지면 이 줄도 함께 빠진다.** 이 파일만 머지되면 규약이
   * 막으려던 상태(클라이언트가 없는 주소를 부른다)가 그대로 생긴다.
   *
   * ⚠️ **`start` 와 달리 이것은 `fetch` 다.** 세션 토큰 원문이 나가는 자리는 이 응답 본문
   * 하나이고, 딥링크에도 로그에도 남지 않는다. */
  appExchange: () => "/api/auth/app/exchange",
  /** 결제사 심사용 아이디 로그인(`W-01b`·`W-01c`, 법무 `L-C30`). **웹만 부르는 `fetch` 다.**
   *  `POST` 에 JSON 본문 `{ loginId, password }` 를 싣고, 성공하면 200 `{ ok: true }` 와 함께
   *  웹 OAuth 로그인과 같은 세션 쿠키가 붙는다. 401·429·404·403·400·503·500 의 계약은
   *  `apps/api/src/routes/review-login.ts` 머리말이 정본이다. 서버의 `REVIEW_LOGIN_PATH` 도
   *  이 줄을 읽는다.
   *
   * **시크릿 셋이 없으면 404 로 닫혀 있는 것이 기본이다.** 404 를 "주소가 틀렸다"로 읽지 않는다.
   *
   * ⚠️ **`appExchange`·`withdraw` 와 같은 성격의 줄이다. 라우트와 한 몸이다.** 라우트가 이
   * 브랜치에서 빠지면 이 줄도 함께 빠진다. 이 파일만 머지되면 머리말의 규약이 막으려던
   * 상태(클라이언트가 없는 주소를 부른다)가 그대로 생긴다. */
  reviewLogin: () => "/api/auth/review/login",
} as const;

/** 로그인 실패 사유가 실리는 질의 이름. **서버가 주소에 실어 보내고 문구는 화면이 고른다**
 *  (`routes/auth.ts` 의 `REASON_BY_CODE`). 값은 `cancelled` · `profile` · `error` 셋이고,
 *  구현 낱말(`access_denied` 같은)을 쓰지 않는 것이 그 셋을 따로 둔 이유다. */
export const LOGIN_REASON_PARAM = "reason";
